import { getActivityUrl, getAppliedProjectUrl } from '../../routing';
import React from 'react';
import LearningPlan from '../LearningPlan/LearningPlan';
import { H3, P } from '../elements/Text';
import StreakCard from './StreakCard';
import styled from 'styled-components';
import {
  AppliedProjectStat,
  JoinedTeamsModule,
  LearningPlan as LearningPlanType,
  OnboardingTeamsModule,
  TeamsContentModule,
} from '../../hydra';
import { useHistory } from 'react-router-dom';
import { getTeamsDashboardUrl } from '../../routing';
import { TeamsProfilePayload, OnboardingTrack } from '../../hydra';
import {
  LEARNING_PLAN_UNIT_TYPE,
  LearningPlanUnit,
} from '../LearningPlan/LearningPlanUnit';
import CTACard from './CTACard';
import day from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Button from '../elements/Button';
import { useProjectsDashboard, useTeamsProfile } from '../../hooks/use-hydra';
import OnboardingModal from '../Onboarding';
import OnboardingTrackCard from './OnboardingTrackCard';
import { ActiveTrackCard } from './TrackCard';
import { AppliedProjects } from '../../pages/ProjectsDashboard';
day.extend(utc);

const GridRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(285px, 1fr));
  gap: 32px;
  margin-top: 36px;

  @media screen and (max-width: 664px) {
    display: flex;
    flex-direction: column;
  }
`;
const CenteredRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 36px;
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(285px, 1fr));
  gap: 32px;
  margin: 32px 0px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

export default function TeamsHome() {
  const { value: projects, loading: isLoadingProjects } =
    useProjectsDashboard();
  const {
    value: teamsProfile,
    retry: refreshTeamsData,
    loading: isLoadingTeams,
  } = useTeamsProfile();

  const [shouldShowAllModules, setShouldShowAllModules] = React.useState(false);

  const history = useHistory();
  const [openTeamsOnboarding, setOpenTeamsOnboarding] =
    React.useState<null | { task: string; track: OnboardingTrack }>(null);

  const learningPlan = teamsProfile?.learningPlan ?? null;
  const shouldShowLearningPlan =
    learningPlan && !isLoadingProjects && !isLoadingTeams;

  const modules = teamsProfile?.contentModules ?? [];
  const appliedProjects = (projects ?? [])
    .filter((p) => (learningPlan ?? []).some((l) => l.slug === p.slug))
    .sort(
      (a, b) =>
        (learningPlan?.findIndex((l) => l.slug === a.slug) ?? 0) -
        (learningPlan?.findIndex((l) => l.slug === b.slug) ?? 0)
    );
  const teamsLearningPlan = useGetLearningPlan({
    learningPlan: teamsProfile?.learningPlan ?? null,
    projects: appliedProjects,
    contentModules: modules,
    setOpenTeamsOnboarding,
  });

  return (
    <React.Fragment>
      <Progress
        plan={teamsLearningPlan}
        teamsProfile={teamsProfile}
        setOpenTeamsOnboarding={setOpenTeamsOnboarding}
      />
      {(learningPlan ?? []).length === 0 && <NoLearningPlan />}

      {shouldShowLearningPlan && (
        <LearningPlan
          infoText={`✨ This unique learning plan is based on your goals and profile. It will update as Enki learns more about you!`}
          learningPlan={teamsLearningPlan}
        />
      )}

      {appliedProjects.length > 0 && (
        <>
          <H3>Projects</H3>
          <AppliedProjects
            appliedProjects={appliedProjects}
            onGoToProject={(slug) => history.push(getAppliedProjectUrl(slug))}
          />
        </>
      )}
      {modules.length > 0 && (
        <>
          <H3>Modules</H3>
          <Modules
            openTeamsOnboarding={openTeamsOnboarding}
            setOpenTeamsOnboarding={setOpenTeamsOnboarding}
            modules={modules.slice(0, shouldShowAllModules ? undefined : 3)}
            refreshTeamsData={refreshTeamsData}
            isRefreshing={isLoadingTeams}
          />
        </>
      )}
      {modules.length > 3 && (
        <CenteredRow>
          <Button
            onClick={() => setShouldShowAllModules(!shouldShowAllModules)}
            label={shouldShowAllModules ? 'Hide Modules' : 'Show All Modules'}
          />
        </CenteredRow>
      )}
    </React.Fragment>
  );
}

function Progress({
  plan,
  teamsProfile,
  setOpenTeamsOnboarding,
}: {
  plan: LearningPlanUnit[];
  teamsProfile: TeamsProfilePayload | null | undefined;
  setOpenTeamsOnboarding: (
    value: {
      task: string;
      track: OnboardingTrack;
    } | null
  ) => void;
}) {
  const history = useHistory();

  const [nextModuleToOnboard] = (teamsProfile?.contentModules ?? [])
    .filter((m) => m.canOnboard && !m.isJoined)
    .sort((a, b) =>
      day.utc(a.startedAt).isBefore(day.utc(b.startedAt)) ? 1 : -1
    );

  if (nextModuleToOnboard) {
    return (
      <GridRow>
        <CTACard
          title={nextModuleToOnboard.content.title}
          nextItemTitle={`Answer a few questions to onboard`}
          description={nextModuleToOnboard.content.description}
          imageUrl={nextModuleToOnboard.content.imageUrl}
          dashboardUrl={null}
          progressText={`${nextModuleToOnboard.lessonsCount} lessons`}
          primaryButton={{
            ctaLabel: 'Start',
            onCTAClick: () =>
              setOpenTeamsOnboarding({
                task: 'track-onboarding',
                track: convertModuleToOnboardingTrack(
                  nextModuleToOnboard as OnboardingTeamsModule
                ),
              }),
          }}
          secondaryButton={null}
        />
        <StreakCard />
      </GridRow>
    );
  }

  const nextPlanItem =
    plan.find((item) => !item.isCompleted) ?? plan[plan.length - 1];

  if (!nextPlanItem) {
    return (
      <GridRow style={{ display: 'block' }}>
        <StreakCard />
      </GridRow>
    );
  }

  return (
    <GridRow>
      <CTACard
        title={nextPlanItem.moduleTitle}
        nextItemTitle={nextPlanItem.nextItemTitle}
        description={nextPlanItem.description}
        imageUrl={nextPlanItem.imageUrl}
        dashboardUrl={nextPlanItem.dashboardUrl}
        progressText={nextPlanItem.progressDescription}
        primaryButton={{
          ctaLabel: !nextPlanItem.isCompleted
            ? nextPlanItem.isInProgress
              ? 'Continue'
              : 'Start'
            : 'Dashboard',
          onCTAClick: nextPlanItem.onClick,
        }}
        secondaryButton={
          nextPlanItem.type === LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE &&
          !nextPlanItem.isCompleted
            ? {
                ctaLabel: 'Dashboard',
                onCTAClick: () => {
                  history.push(nextPlanItem.dashboardUrl ?? '');
                },
              }
            : null
        }
      />
      <StreakCard />
    </GridRow>
  );
}

function Modules({
  openTeamsOnboarding,
  setOpenTeamsOnboarding,
  modules,
  refreshTeamsData,
  isRefreshing,
}: {
  openTeamsOnboarding: { task: string; track: OnboardingTrack } | null;
  setOpenTeamsOnboarding: (
    value: {
      task: string;
      track: OnboardingTrack;
    } | null
  ) => void;
  modules: Array<TeamsContentModule>;
  refreshTeamsData: () => void;
  isRefreshing: boolean;
}) {
  const onboardingModules = modules.filter(
    (m) => m.canOnboard && !m.isJoined
  ) as OnboardingTeamsModule[];

  const joinedModules = modules.filter(
    (m) => m.isJoined && !m.canOnboard
  ) as JoinedTeamsModule[];

  return (
    <CardsContainer>
      {openTeamsOnboarding && (
        <OnboardingModal
          onRefreshData={refreshTeamsData}
          onboardingTask={openTeamsOnboarding}
          onClose={() => setOpenTeamsOnboarding(null)}
          isRefreshing={isRefreshing}
        />
      )}

      {onboardingModules.map((m) => {
        // converting to the old onboarding track format so we dont need to refactor the onboarding code
        const onboardingTrack: OnboardingTrack =
          convertModuleToOnboardingTrack(m);
        return (
          <OnboardingTrackCard
            key={`onboarding-track-card-${onboardingTrack.slug}`}
            track={onboardingTrack}
            setOnboardingTrack={() =>
              setOpenTeamsOnboarding({
                task: 'track-onboarding', // this is redundant atm, as the community onboarding has been deprecated
                track: onboardingTrack,
              })
            }
          />
        );
      })}
      {joinedModules.map((m) => {
        return (
          <ActiveTrackCard key={`track-card-${m.content.slug}`} module={m} />
        );
      })}
    </CardsContainer>
  );
}

function useGetLearningPlan({
  learningPlan,
  projects,
  contentModules,
  setOpenTeamsOnboarding,
}: {
  learningPlan: LearningPlanType | null;
  projects: Array<AppliedProjectStat>;
  contentModules: Array<TeamsContentModule>;
  setOpenTeamsOnboarding: (
    value: {
      task: string;
      track: OnboardingTrack;
    } | null
  ) => void;
}): Array<LearningPlanUnit> {
  const history = useHistory();

  const learningPlanUnits: Array<LearningPlanUnit> = (learningPlan ?? [])
    .map((unit) => {
      if (unit.type === 'project') {
        const project = projects.find((p) => p.slug === unit.slug);

        if (!project) {
          return null;
        }

        const isCompleted =
          project.completedQuestions === project.question_count;
        const isInProgress =
          (project?.completedQuestions ?? -1) > 0 &&
          (project?.completedQuestions ?? -1) < project.question_count;
        const progressDescription =
          project.completedQuestions === project.question_count ||
          !project.completedQuestions
            ? `${project.question_count} exercises`
            : `${project?.completedQuestions ?? 0}/${
                project.question_count
              } exercises`;

        return {
          type: LEARNING_PLAN_UNIT_TYPE.PROJECT,
          unitTitle: project?.name ?? '',
          nextItemTitle: project?.name ?? '',
          moduleTitle: project?.topic ?? '',
          subtitle: 'PROJECT',
          slug: unit.slug,
          imageUrl: project?.image_url ?? '',
          description: project?.description ?? '',
          reasoning: unit.reasoning,
          progressDescription,
          isCompleted,
          isInProgress,
          onClick: async () => {
            history.push(getAppliedProjectUrl(project.slug));
          },
          dashboardUrl: null,
          isLocked: false,
        };
      }

      if (unit.type === 'module') {
        const module = contentModules.find((m) => m.content.slug === unit.slug);
        if (!module) {
          return null;
        }

        const isCompleted =
          module?.coreAttemptedLessonsCount === module?.coreLessonsCount;
        const isInProgress =
          module?.coreAttemptedLessonsCount > 0 &&
          module?.coreAttemptedLessonsCount < module?.coreLessonsCount;

        const progressDescription =
          module?.coreAttemptedLessonsCount === 0 ||
          module?.coreAttemptedLessonsCount === module?.coreLessonsCount
            ? `${module?.coreLessonsCount} lessons`
            : `${module?.coreAttemptedLessonsCount}/${module?.coreLessonsCount} lessons`;

        return {
          type: LEARNING_PLAN_UNIT_TYPE.CONTENT_MODULE,
          unitTitle: module.content.title,
          nextItemTitle:
            module.firstIncompleteActivity?.activityName ||
            module.content.title,
          moduleTitle: module.content.title,
          subtitle: 'MODULE',
          description: module.content.description ?? '',
          slug: unit.slug,
          imageUrl: module.content.imageUrl ?? '',
          reasoning: unit.reasoning,
          progressDescription,
          isCompleted,
          isInProgress,
          onClick: async () => {
            if (module.canOnboard) {
              setOpenTeamsOnboarding({
                task: 'track-onboarding',
                track: convertModuleToOnboardingTrack(module),
              });
            } else if (
              module.firstUnattemptedActivity?.missionSlug &&
              module.firstUnattemptedActivity?.activitySlug
            ) {
              history.push(
                getActivityUrl(
                  module.trackSlug ?? '',
                  module.firstUnattemptedActivity?.missionSlug,
                  module.firstUnattemptedActivity?.activitySlug
                )
              );
            } else {
              // When they have already completed the entire module
              history.push(getTeamsDashboardUrl(module.trackSlug ?? ''));
            }
          },
          dashboardUrl: getTeamsDashboardUrl(module.trackSlug ?? ''),
          isLocked: !(module.isJoined || module.canOnboard),
        };
      }

      throw new Error(
        `Invalid learning unit type: ${(unit as { type: string }).type}`
      );
    })
    .filter((unit): unit is NonNullable<typeof unit> => unit !== null);

  return learningPlanUnits;
}

function convertModuleToOnboardingTrack(
  module: OnboardingTeamsModule
): OnboardingTrack {
  const onboardingTrack: OnboardingTrack = {
    onboarding: module.onboarding,
    name: module.content.title,
    slug: module.content.slug,
    contentTitle: module.content.title,
    topic: module.content.topic,
    imageUrl: module.content.imageUrl,
    description: module.content.description,
    startedAt: module.startedAt,
    endedAt: module.endedAt,
    contentId: module.content.id,
    contentSlug: module.content.slug,
    lessonsCount: module.lessonsCount,
    id: module.trackId,
    coreLessonsCount: module.coreLessonsCount,
    completedLessonsCount: module.completedLessonsCount,
    coreCompletedLessonsCount: module.coreCompletedLessonsCount,
    attemptedLessonsCount: module.attemptedLessonsCount,
    coreAttemptedLessonsCount: module.coreAttemptedLessonsCount,
  };
  return onboardingTrack;
}

const WarningIcon = styled.i.attrs({
  className: 'fa-solid fa-lightbulb',
})`
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 16px;
  padding-bottom: 0;
  margin-top: 16px;
`;

function NoLearningPlan() {
  return (
    <Row>
      <WarningIcon />
      <P>
        It seems you don't have a learning plan yet. Get in contact with the
        Enki team to get set up.
      </P>
    </Row>
  );
}
